import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Check, X, Loader, AlertCircle, FileCheck, Receipt } from "lucide-react";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
};

const fmtCLP  = (n) => n != null ? `$${Number(n).toLocaleString("es-CL")}` : "—";
const fmtDate = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"}) : "—";
const calcSub = (items) => items.reduce((s,i) => s + (i.horas||0)*(i.ph||0), 0);
const calcTot = (items) => calcSub(items) + Math.round(calcSub(items)*0.19);

const ST_COLORS = {
  pagada:    { color:"#059669", bg:"#d1fae5" },
  emitida:   { color:"#0284c7", bg:"#e0f2fe" },
  pendiente: { color:"#d97706", bg:"#fef3c7" },
  vencida:   { color:"#dc2626", bg:"#fee2e2" },
};

export default function AsociarFacturaModal({ cotizaciones, onClose, onConfirm }) {
  const [facturas,     setFacturas]     = useState([]);
  const [seleccionada, setSeleccionada] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState(null);

  const rut     = cotizaciones[0]?.rut;
  const cliente = cotizaciones[0]?.cliente;
  const totalCots = cotizaciones.reduce((s, q) => {
    const items = q.items || [];
    return s + (q.con_iva !== false ? calcTot(items) : calcSub(items));
  }, 0);

  const notify = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("ingresos")
        .select("id, folio, fecha_emision, monto_total, estado")
        .eq("rut_cliente", rut)
        .order("fecha_emision", { ascending: false });
      if (error) {
        console.error("Error Supabase ingresos:", error);
        notify("Error al cargar facturas", "err");
      } else setFacturas(data || []);
      setLoading(false);
    })();
  }, [rut]);

  const confirmar = async () => {
    if (!seleccionada) return;
    setSaving(true);
    const { error } = await supabase
      .from("cotizaciones")
      .update({ ingreso_id: seleccionada.id })
      .in("id", cotizaciones.map(c => c.id));
    if (error) {
      notify("Error al asociar", "err");
      console.error(error);
      setSaving(false);
      return;
    }
    onConfirm();
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9990,
      background:"rgba(15,23,42,0.5)", backdropFilter:"blur(3px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"24px 16px",
    }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinning{animation:spin 1s linear infinite}
      `}</style>

      {toast && (
        <div style={{
          position:"fixed", top:20, right:20, zIndex:9999,
          display:"flex", alignItems:"center", gap:8,
          background:C.white,
          border:`1px solid ${toast.type==="err"?C.err:C.ok}40`,
          borderLeft:`3px solid ${toast.type==="err"?C.err:C.ok}`,
          borderRadius:8, padding:"11px 16px", fontSize:14,
          color:toast.type==="err"?C.err:C.ok,
          boxShadow:"0 4px 16px #00000014",
        }}>
          {toast.type==="err" ? <AlertCircle size={15}/> : <Check size={15}/>}
          {toast.msg}
        </div>
      )}

      <div style={{
        background:C.white, borderRadius:16, width:"100%", maxWidth:560,
        boxShadow:"0 24px 80px #00000022", border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease", fontFamily:"'DM Sans',sans-serif",
      }}>

        {/* Header */}
        <div style={{
          padding:"20px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <div style={{display:"flex", alignItems:"center", gap:12}}>
            <div style={{width:36, height:36, borderRadius:9, background:C.okL,
              display:"flex", alignItems:"center", justifyContent:"center"}}>
              <Receipt size={18} color={C.ok}/>
            </div>
            <div>
              <div style={{fontWeight:700, fontSize:16, color:C.text}}>
                Asociar a factura
              </div>
              <div style={{fontSize:12, color:C.sub, marginTop:2}}>
                {cliente} · {cotizaciones.length} cotización{cotizaciones.length!==1?"es":""} seleccionada{cotizaciones.length!==1?"s":""}
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{background:"transparent", border:"none", cursor:"pointer",
              color:C.light, padding:4, borderRadius:6, display:"flex"}}
            onMouseEnter={e => e.currentTarget.style.color = C.text}
            onMouseLeave={e => e.currentTarget.style.color = C.light}>
            <X size={18}/>
          </button>
        </div>

        <div style={{padding:"20px 24px"}}>

          {/* Resumen */}
          <div style={{
            background:C.accentL, border:`1px solid ${C.accent}30`,
            borderRadius:10, padding:"12px 16px", marginBottom:18,
            display:"flex", alignItems:"center", justifyContent:"space-between",
          }}>
            <div style={{fontSize:13, color:C.text}}>
              <strong>{cotizaciones.length}</strong> cotización{cotizaciones.length!==1?"es":""} seleccionada{cotizaciones.length!==1?"s":""}
            </div>
            <div style={{fontWeight:700, fontSize:15, color:C.accent}}>
              Total: {fmtCLP(totalCots)}
            </div>
          </div>

          {/* Lista facturas */}
          <div style={{fontSize:12, fontWeight:600, color:C.sub,
            letterSpacing:.4, textTransform:"uppercase", marginBottom:8}}>
            Seleccionar factura de {cliente}
          </div>

          <div style={{maxHeight:300, overflowY:"auto", borderRadius:10, border:`1px solid ${C.border}`}}>
            {loading && (
              <div style={{padding:"24px", textAlign:"center", color:C.sub,
                fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:8}}>
                <Loader size={15} className="spinning"/> Cargando facturas...
              </div>
            )}

            {!loading && facturas.length === 0 && (
              <div style={{padding:"24px", textAlign:"center", fontSize:13, color:C.light}}>
                No hay facturas registradas para este cliente
              </div>
            )}

            {!loading && facturas.map((f, idx) => {
              const selected = seleccionada?.id === f.id;
              const stStyle  = ST_COLORS[f.estado?.toLowerCase()] || { color:C.sub, bg:C.bg };
              return (
                <div key={f.id}
                  onClick={() => setSeleccionada(f)}
                  style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"12px 14px", cursor:"pointer",
                    borderBottom: idx < facturas.length-1 ? `1px solid ${C.border}` : "none",
                    background: selected ? C.accentL : C.white,
                    transition:"background .1s",
                  }}
                  onMouseEnter={e => { if (!selected) e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={e => { if (!selected) e.currentTarget.style.background = C.white; }}>
                  <input
                    type="radio"
                    checked={selected}
                    onChange={() => setSeleccionada(f)}
                    style={{cursor:"pointer", accentColor:C.accent, flexShrink:0}}
                  />
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontWeight:600, fontSize:14, color:C.text}}>
                      {f.folio ? `Folio ${f.folio}` : `Factura ${f.id.slice(0,8)}`}
                    </div>
                    <div style={{fontSize:12, color:C.sub, marginTop:2}}>
                      {fmtDate(f.fecha_emision)}
                    </div>
                  </div>
                  <div style={{textAlign:"right", flexShrink:0}}>
                    <div style={{fontWeight:700, fontSize:14, color:C.text}}>
                      {fmtCLP(f.monto_total)}
                    </div>
                    {f.estado && (
                      <span style={{
                        fontSize:11, fontWeight:500,
                        color:stStyle.color, background:stStyle.bg,
                        padding:"1px 7px", borderRadius:20, marginTop:2,
                        display:"inline-block",
                      }}>
                        {f.estado}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Acciones */}
          <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:22}}>
            <button onClick={onClose}
              style={{padding:"9px 22px", borderRadius:8,
                border:`1px solid ${C.border}`, background:"transparent",
                color:C.sub, fontSize:14, cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              Cancelar
            </button>
            <button onClick={confirmar}
              disabled={!seleccionada || saving}
              style={{
                display:"flex", alignItems:"center", gap:7,
                padding:"9px 24px", borderRadius:8, border:"none",
                background: !seleccionada || saving ? "#94a3b8" : C.ok,
                color:"#fff", fontWeight:600, fontSize:14,
                cursor: !seleccionada || saving ? "not-allowed" : "pointer",
                fontFamily:"'DM Sans',sans-serif",
                boxShadow: !seleccionada || saving ? "none" : `0 4px 12px ${C.ok}40`,
                transition:"background .15s",
              }}
              onMouseEnter={e => { if (seleccionada && !saving) e.currentTarget.style.background = "#059669"; }}
              onMouseLeave={e => { if (seleccionada && !saving) e.currentTarget.style.background = C.ok; }}>
              {saving
                ? <><Loader size={14} className="spinning"/>Asociando...</>
                : <><FileCheck size={15}/>Confirmar asociación</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
